import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import AnswerLogModal from "@/components/AnswerLogModal";
import BulkAssignTniModal from "@/components/BulkAssignTniModal";
import FYSelect from "@/components/FYSelect";
import { loadFinancialYears, fyLabel } from "@/lib/fy";
import { Check, X, FileDown, Loader2, ClipboardPlus } from "lucide-react";
import { toast } from "sonner";

export default function CompetencyCard() {
  const { user } = useAuth();
  const params = useParams();
  const employeeId = params.employeeId ? parseInt(params.employeeId) : user.employee_id;
  const [card, setCard] = useState(null);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [activeAnswerLogId, setActiveAnswerLogId] = useState(null);
  const [fy, setFy] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const canAssign = ["hr", "super_admin"].includes(user.system_role);

  useEffect(() => {
    loadFinancialYears().then((d) => setFy((prev) => prev ?? d.current_fy));
  }, []);

  useEffect(() => {
    if (fy === null) return;
    api.get(`/competency-card/${employeeId}`, { params: { fy } })
      .then((r) => setCard(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load"));
  }, [employeeId, fy, reloadTick]);

  // Count of unique skills needing a TNI test (gap/not_assessed across roles).
  const pendingCount = useMemo(() => {
    if (!card) return 0;
    const seen = new Set();
    for (const r of card.roles || []) {
      for (const s of r.skills || []) {
        if (s.status === "gap" || s.status === "not_assessed") seen.add(s.skill_code);
      }
    }
    return seen.size;
  }, [card]);

  const exportPdf = async () => {
    setDownloading(true);
    try {
      const qs = fy !== null ? `?fy=${fy}` : "";
      const res = await fetch(`${API}/competency-card/${employeeId}/pdf${qs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const fallback = `CompetencyCard_${card.employee.name.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
      const filename = match ? match[1] : fallback;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch (e) {
      toast.error("Export failed — please try again");
    } finally {
      setDownloading(false);
    }
  };

  if (err) return <div className="p-10 text-sm text-[#C41E1E]">{err}</div>;
  if (!card) return <div className="p-10 text-sm text-[#666666]">Loading…</div>;

  return (
    <div data-testid="competency-card-page">
      <PageHeader
        eyebrow="Competency Card"
        title={
          <span className="inline-flex items-center gap-3">
            {card.employee.name}
            {card.employee.workforce_type && (
              <span
                data-testid="card-workforce-badge"
                className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border ${
                  card.employee.workforce_type === "worker"
                    ? "bg-amber-50 text-amber-800 border-amber-200"
                    : "bg-sky-50 text-sky-800 border-sky-200"
                }`}
              >
                {card.employee.workforce_type === "worker" ? "Operator" : "Staff"}
              </span>
            )}
          </span>
        }
        subtitle={`${card.employee.department} · ${card.employee.roles.join(", ")}`}
        breadcrumbs={
          // Employees only see their own card, no /employees link to drill from
          user.system_role === "employee" || employeeId === user.employee_id
            ? [{ label: "Home", to: "/" }, { label: "My Card" }]
            : [
                { label: "Home", to: "/" },
                { label: "Employees", to: "/employees" },
                { label: `${card.employee.name} — Competency Card` },
              ]
        }
        action={
          <div className="flex items-end gap-2">
            <FYSelect value={fy} onChange={setFy} testid="card-fy-select" label="" />
            {canAssign && pendingCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAssignModal(true)}
                data-testid="assign-all-tni-btn"
                className="inline-flex items-center gap-1.5 bg-[#C41E1E] hover:bg-[#a71919] text-white text-sm rounded-sm px-3 py-2"
              >
                <ClipboardPlus size={14} />
                Assign All Pending TNI Tests ({pendingCount})
              </button>
            )}
            <button
              type="button"
              onClick={exportPdf}
              disabled={downloading}
              data-testid="export-pdf-button"
              className="inline-flex items-center gap-1.5 bg-[#0D0D0D] hover:bg-neutral-800 text-white text-sm rounded-sm px-3 py-2 disabled:opacity-50"
            >
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
              {downloading ? "Generating…" : "Export PDF"}
            </button>
          </div>
        }
      />
      <div className="px-4 md:px-10 py-8 space-y-6">
        {card.roles.length === 0 && (
          <div className="bc-card p-6 text-sm text-[#666666]">No competency profile assigned yet.</div>
        )}
        {card.roles.map((r) => (
          <div key={r.role} className="bc-card" data-testid={`role-card-${r.role}`}>
            <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
              <div>
                <div className="bc-label">Role</div>
                <h3 className="font-heading text-lg font-semibold mt-0.5">{r.role}</h3>
              </div>
              <div className="text-xs text-[#666666]">
                {r.skills.length === 0
                  ? <span className="text-[#999999] italic">No requirements</span>
                  : `${r.skills.filter((s) => s.status === "competent").length}/${r.skills.length} competent`
                }
              </div>
            </div>
            {r.skills.length === 0 ? (
              <div
                className="px-5 py-8 text-sm text-[#999999] text-center"
                data-testid={`no-requirements-${r.role}`}
              >
                No competency requirements are defined for this role in the competency map.
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F9F9F9] border-b border-[#E5E5E5]">
                    <th className="text-left px-5 py-2.5 bc-label">Competency</th>
                    <th className="text-center px-5 py-2.5 bc-label">Required</th>
                    <th className="text-center px-5 py-2.5 bc-label">Achieved</th>
                    <th className="text-left px-5 py-2.5 bc-label">Status</th>
                    <th className="text-left px-5 py-2.5 bc-label">Last test</th>
                  </tr>
                </thead>
                <tbody>
                  {r.skills.map((s) => (
                    <tr key={s.skill_code} className="border-b border-[#E5E5E5] bc-row-hover">
                      <td className="px-5 py-2.5">{s.skill_name}</td>
                      <td className="px-5 py-2.5 text-center font-mono">{s.required_level}</td>
                      <td className="px-5 py-2.5 text-center font-mono">{s.achieved_level || "—"}</td>
                      <td className="px-5 py-2.5">
                        {s.status === "competent" ? (
                          <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium"><Check size={14} /> Competent</span>
                        ) : s.status === "gap" ? (
                          <span className="inline-flex items-center gap-1 text-[#C41E1E] text-xs font-medium"><X size={14} /> GAP</span>
                        ) : (
                          <StatusPill status="not_assessed" />
                        )}
                      </td>
                      <td className="px-5 py-2.5 text-xs text-[#666666]">
                        {s.last_completed_date ? (
                          <span className="inline-flex items-center gap-2">
                            <span>{s.last_completed_date}</span>
                            {s.last_assessment_id && (
                              <button
                                type="button"
                                onClick={() => setActiveAnswerLogId(s.last_assessment_id)}
                                data-testid={`view-answers-${s.skill_code}`}
                                className="text-[#C41E1E] hover:underline font-medium"
                              >
                                View Answers
                              </button>
                            )}
                          </span>
                        ) : (
                          <span data-testid={`not-assessed-${s.skill_code}`}>Not Assessed ({fyLabel(fy)})</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        ))}
      </div>
      <AnswerLogModal
        assessmentId={activeAnswerLogId}
        onClose={() => setActiveAnswerLogId(null)}
      />
      <BulkAssignTniModal
        open={showAssignModal}
        employee={card ? { employee_id: employeeId, name: card.employee.name } : null}
        onClose={() => setShowAssignModal(false)}
        onSuccess={() => setReloadTick((t) => t + 1)}
      />
    </div>
  );
}
